/**
 * A Scheduler (obj_type "H") opened from the navigation menu.
 *
 * The end-user half of the same object the designer authors: next run, what
 * the job does, its history and its results — plus Run now for somebody with
 * the right. Read-only about the DESIGN (there is no editing here); the runs
 * are the live part.
 *
 * Host-agnostic: the platform Run view and a downloaded runtime mount this
 * same component and differ only in the `SchedulerRuntime` adapter they pass.
 */
import { useCallback, useEffect, useState } from 'react'
import type { SchedulerRuntime, ScheduleView } from './contract'
import RunsPanel from './RunsPanel'
import {
  C, Chip, DriftNotice, Empty, cardStyle, formatRelative, formatWhen, labelStyle,
} from './ui'

export default function SchedulerRunPane({ runtime, objName }: {
  runtime: SchedulerRuntime
  objName: string
}) {
  const [schedule, setSchedule] = useState<ScheduleView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    try {
      setSchedule(await runtime.get(objName))
      setError(null)
    } catch (e: unknown) {
      setError(extractMessage(e, 'Could not load this scheduler'))
    }
  }, [runtime, objName])

  useEffect(() => { void reload() }, [reload])

  const start = useCallback(async () => {
    if (!runtime.runNow) return
    setBusy(true)
    setActionError(null)
    try {
      await runtime.runNow(objName)
      runtime.notify?.('success', 'Started — watch the history below')
      window.setTimeout(() => { void reload() }, 1500)
    } catch (e: unknown) {
      const message = extractMessage(e, 'Could not start the run')
      // Shown in place as well as toasted: a host without a toast adapter
      // would otherwise swallow the only feedback the button gives.
      setActionError(message)
      runtime.notify?.('error', message)
    } finally {
      setBusy(false)
    }
  }, [runtime, objName, reload])

  if (error) {
    return <Empty title="This scheduler is not available" hint={error} />
  }
  if (!schedule) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: C.ink2,
      fontSize: '0.9125rem' }}>Loading…</div>
  }

  const steps = [schedule.target, ...(schedule.then || [])]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {schedule.description && (
            <div style={{ fontSize: '0.8825rem', color: C.ink2 }}>
              {schedule.description}
            </div>
          )}
        </div>
        <Chip tone={schedule.enabled ? 'ok' : 'muted'}>
          {schedule.enabled ? 'Enabled' : 'Disabled'}
        </Chip>
        {runtime.runNow && (
          <button type="button" onClick={() => void start()} disabled={busy}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, height: 30,
              padding: '0 12px', borderRadius: 8, fontSize: '0.8425rem',
              fontWeight: 600, cursor: busy ? 'default' : 'pointer',
              border: `1px solid ${C.accent}`, background: C.accent,
              color: '#fff', opacity: busy ? 0.6 : 1,
            }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 4.5v15l13-7.5z" />
            </svg>
            {busy ? 'Starting…' : 'Run now'}
          </button>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))',
        gap: 1, background: C.line, border: `1px solid ${C.line}`,
        borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ background: C.panel, padding: '12px 14px' }}>
          <div style={labelStyle}>Next run</div>
          <div style={bandValue}>
            {schedule.next_run ? formatWhen(schedule.next_run) : 'Not scheduled'}
          </div>
          <div style={bandSub}>
            {schedule.next_run ? formatRelative(schedule.next_run)
              : 'this scheduler is disabled'}
          </div>
        </div>
        <div style={{ background: C.panel, padding: '12px 14px' }}>
          <div style={labelStyle}>Last run</div>
          <div style={{ ...bandValue,
            color: schedule.last_run?.status === 'failed' ? C.err
              : schedule.last_run?.status === 'succeeded' ? C.ok : C.ink }}>
            {schedule.last_run
              ? schedule.last_run.status.charAt(0).toUpperCase()
                + schedule.last_run.status.slice(1)
              : 'Never run'}
          </div>
          <div style={bandSub}>
            {schedule.last_run ? formatWhen(schedule.last_run.queued_at) : ''}
          </div>
        </div>
        <div style={{ background: C.panel, padding: '12px 14px' }}>
          <div style={labelStyle}>Cadence</div>
          <div style={bandValue}>{schedule.cadence_text}</div>
          <div style={{ ...bandSub, fontFamily: 'var(--font-mono)' }}>
            {schedule.schedule?.cron_expression || ''}
          </div>
        </div>
      </div>

      <DriftNotice drift={schedule.timezone_drift} />

      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 14px', borderBottom: `1px solid ${C.line}` }}>
          <div style={{ fontSize: '0.875rem', fontWeight: 600, flex: 1 }}>
            What this job does
          </div>
          <Chip tone="muted">{steps.length} step{steps.length === 1 ? '' : 's'}</Chip>
        </div>
        {steps.map((step, i) => (
          <div key={i} style={{ display: 'flex', gap: 11, padding: '11px 14px',
            borderBottom: i < steps.length - 1 ? `1px solid ${C.line}` : 'none' }}>
            <div style={{ width: 22, height: 22, borderRadius: 999, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.8125rem', fontWeight: 700, background: C.s2,
              color: C.ink2, border: `1px solid ${C.line2}`, marginTop: 1 }}>
              {i + 1}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '0.9025rem', fontWeight: 600 }}>
                {step.kind} · {step.obj_name || step.function}
              </div>
              <div style={{ fontSize: '0.8225rem', color: C.ink2, marginTop: 1 }}>
                {step.format ? step.format.toUpperCase() : ''}
                {step.deliver?.dms_folder
                  ? `${step.format ? ' · ' : ''}files to ${step.deliver.dms_repo || '?'}/${step.deliver.dms_folder}`
                  : ''}
              </div>
            </div>
          </div>
        ))}
      </div>

      {actionError && (
        <div style={{
          background: C.errWeak, border: `1px solid ${C.err}33`, borderRadius: 8,
          padding: '10px 12px', color: C.err, fontSize: '0.8625rem',
        }}>{actionError}</div>
      )}

      <RunsPanel runtime={runtime} scheduleName={objName} pollMs={4000} />
    </div>
  )
}

const bandValue = {
  fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.25, marginTop: 3,
} as const
const bandSub = {
  fontSize: '0.8125rem', color: C.ink2, marginTop: 2,
} as const

/** An error message from whatever the host's client threw — an axios-shaped
 *  response, a fetch Error, or a plain string. */
export function extractMessage(err: unknown, fallback: string): string {
  const shaped = err as { response?: { data?: { detail?: string } }; message?: string }
  return shaped?.response?.data?.detail || shaped?.message || fallback
}
